import type { Workflow, Agent, Team, Trigger } from "@/stores/WorkflowStore";
import type { PlaygroundNode, PlaygroundEdge } from "./workflowToNodes";

/**
 * Reconstructs a Workflow object from ReactFlow nodes and edges.
 * Used when saving the playground canvas back to the backend.
 *
 * @param nodes - Current ReactFlow nodes
 * @param edges - Current ReactFlow edges
 * @param baseWorkflow - The original workflow to merge metadata from (name, id, tenant, etc.)
 */
export const nodesToWorkflow = (
  nodes: PlaygroundNode[],
  edges: PlaygroundEdge[],
  baseWorkflow: Partial<Workflow>,
): Workflow => {
  const agents: Record<string, Agent> = {};
  const teams: Record<string, Team> = {};
  let input: Record<string, any> | null = null;

  // Extract input node
  const inputNode = nodes.find((n) => n.type === "input");

  if (inputNode?.data.inputParams && Object.keys(inputNode.data.inputParams).length > 0) {
    input = inputNode.data.inputParams;
  }

  // Extract team nodes
  const teamNodes = nodes.filter((n) => n.type === "team");

  teamNodes.forEach((teamNode) => {
    const teamId = teamNode.id;

    // Build triggers from edges targeting this team
    const triggers = buildTriggersFromEdges(teamId, edges, nodes);

    teams[teamId] = {
      members: teamNode.data.teamMembers || [],
      strategy: teamNode.data.teamStrategy,
      maxCycles: teamNode.data.teamMaxCycles,
      goal: teamNode.data.teamGoal || "",
      useLlmJudgment: teamNode.data.teamUseLlmJudgment,
      selectorPrompt: teamNode.data.teamSelectorPrompt,
      aggregatorPrompt: teamNode.data.teamAggregatorPrompt,
      triggers,
      model: teamNode.data.teamModel,
    };
  });

  // Extract agent nodes
  const agentNodes = nodes.filter((n) => n.type === "agent");

  agentNodes.forEach((agentNode) => {
    const agentId = agentNode.data.agentId || agentNode.id;

    // Find tools connected to this agent from edges
    const connectedTools = edges
      .filter((edge) => edge.target === agentNode.id)
      .map((edge) => {
        const sourceNode = nodes.find((n) => n.id === edge.source);

        return sourceNode?.type === "tool" ? sourceNode.data.toolId : null;
      })
      .filter((toolId): toolId is string => toolId !== null && toolId !== undefined);

    // Merge existing tools from parameters with connected tools (deduplicate)
    const existingTools = (agentNode.data.parameters?.tools as string[]) || [];
    const allTools = [...new Set([...existingTools, ...connectedTools])];

    // Build triggers from edges targeting this agent
    const triggers = buildTriggersFromEdges(agentNode.id, edges, nodes);

    agents[agentId] = {
      type: agentNode.data.agentType || "chat_agent",
      parameters: {
        ...agentNode.data.parameters,
        tools: allTools.length > 0 ? allTools : undefined,
      },
      triggers,
      description: agentNode.data.description,
    };
  });

  // Also include team member agents that may not be on the canvas directly
  // (they exist in the workflow but are rendered inside team nodes)
  if (baseWorkflow.agents) {
    const teamMemberIds = new Set<string>();

    Object.values(teams).forEach((team) => {
      team.members.forEach((memberId) => {
        if (!Object.keys(teams).includes(memberId)) {
          teamMemberIds.add(memberId);
        }
      });
    });

    teamMemberIds.forEach((memberId) => {
      if (!agents[memberId] && baseWorkflow.agents?.[memberId]) {
        agents[memberId] = { ...baseWorkflow.agents[memberId] };
      }
    });
  }

  return {
    name: baseWorkflow.name || "New Workflow",
    id: baseWorkflow.id || "",
    tenant: baseWorkflow.tenant || "",
    description: baseWorkflow.description || "",
    input,
    teams: Object.keys(teams).length > 0 ? teams : undefined,
    agents,
  };
};

/**
 * Builds trigger array for a given target node from edges.
 * Only includes triggers from agent/team/input sources (not tools).
 */
function buildTriggersFromEdges(
  targetNodeId: string,
  edges: PlaygroundEdge[],
  nodes: PlaygroundNode[],
): Trigger[] {
  const triggers: Trigger[] = [];

  for (const edge of edges) {
    if (edge.target !== targetNodeId) continue;

    const sourceNode = nodes.find((n) => n.id === edge.source);

    // Only create triggers for agent, team, or input sources
    if (
      sourceNode &&
      (sourceNode.type === "agent" || sourceNode.type === "team" || sourceNode.type === "input")
    ) {
      // Read mapping from edge data if available, otherwise use default
      const edgeMapping = (edge.data as Record<string, any>)?.mapping;
      const mapping =
        edgeMapping && Object.keys(edgeMapping).length > 0 ? edgeMapping : { prompt: "data" };

      triggers.push({
        source: edge.source,
        mapping,
      });
    }
  }

  return triggers;
}

/**
 * Creates a blank workflow configuration for a new playground session.
 */
export const createBlankWorkflow = (): Omit<Workflow, "tenant"> => ({
  name: "New Workflow",
  id: "",
  description: "",
  input: {},
  agents: {},
});
